/* FILE: ./frontend/src/features/auth/RegisterForm.jsx */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import axios from 'axios';
import { Turnstile } from '@marsidev/react-turnstile';

const RegisterForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('email'); 
  
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    otp: '',
    company_name: '',
    contact_person: '', // ⚡ SYNCED: Perfectly matches your DB schema now
    tosAgreed: false 
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const returnTo = location.state?.returnTo || sessionStorage.getItem('returnTo');

  useEffect(() => {
    if (returnTo) {
        sessionStorage.setItem('returnTo', returnTo);
    }
  }, [returnTo]);

  // ⚡ FIX: Auto-Auth Check now properly looks for 'contact_person'
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
        if (!profile?.contact_person || !profile?.company_name) {
          setStep('profile');
        } else {
          // ⚡ HARD RELOAD FIX: Forces context memory to refresh
          if (session.user.email?.toLowerCase() === 'admin@shaikh.net.in' || profile?.role === 'admin') {
            window.location.href = '/admin';
          } else {
            sessionStorage.removeItem('returnTo');
            window.location.href = returnTo || '/directory';
          }
        }
      }
    };
    checkUser();
  }, [navigate, returnTo]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [e.target.name]: value }));
  };

  const logRegistrationFailure = async (email, reason, rawError = null) => {
    try {
      const browserData = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      };
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      await axios.post(`${apiUrl}/verification/registration-failure`, {
        email: email || 'No email provided',
        reason: reason,
        browserData: browserData,
        rawError: rawError ? rawError.toString() : null
      });
    } catch (err) {}
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const blockedDomains = [
        'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
        'aol.com', 'icloud.com', 'protonmail.com', 'zoho.com', 
        'yandex.com', 'mail.com', 'msn.com', 'live.com'
      ];
      const emailDomain = formData.email.split('@')[1]?.toLowerCase();
      if (blockedDomains.includes(emailDomain)) {
        logRegistrationFailure(formData.email, 'Corporate Email Block');
        throw new Error(t('auth.err_business_email_required', 'Please use your corporate email address to access the B2B network.'));
      }

      if (!turnstileToken) {
        logRegistrationFailure(formData.email, 'Cloudflare Token Missing');
        throw new Error(t('auth.err_sec_token', 'Security verification in progress. Please wait a second and try again.'));
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

      try {
        await axios.post(`${apiUrl}/verification/check-ban`, { 
            email: formData.email,
            cf_turnstile_token: turnstileToken 
        });
      } catch (banErr) {
        logRegistrationFailure(formData.email, 'Check Ban API Failure', banErr.response?.data);
        if (banErr.response?.status === 403) throw new Error(banErr.response?.data?.error || 'This email is restricted.');
        throw new Error('Security check failed. Please try again.');
      }

      const { error: authError } = await supabase.auth.signInWithOtp({ email: formData.email });
      if (authError) throw authError;

      setStep('otp');
      setLoading(false);
    } catch (error) {
      setError(error.message || t('auth.err_unexp', 'An unexpected error occurred.'));
      setTurnstileToken(null); 
      if (turnstileRef.current) turnstileRef.current.reset();
      setLoading(false);
    } 
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { session }, error: verifyError } = await supabase.auth.verifyOtp({ 
        email: formData.email, 
        token: formData.otp, 
        type: 'email' 
      });

      if (verifyError) throw verifyError;

      const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
      
      // ⚡ FIX: Verify against the correct DB columns
      if (!profile?.contact_person || !profile?.company_name) {
        setStep('profile');
      } else {
        // ⚡ HARD RELOAD FIX: Forces context memory to refresh
        if (formData.email.toLowerCase() === 'admin@shaikh.net.in' || profile?.role === 'admin') {
          window.location.href = '/admin';
        } else {
          sessionStorage.removeItem('returnTo'); 
          window.location.href = returnTo || '/directory';
        }
      }
    } catch (error) {
      setError(error.message || 'Verification failed. Please check your code.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.tosAgreed) throw new Error('You must agree to the Terms of Service.');

      const { data: { session } } = await supabase.auth.getSession();
      
      const { error: upsertError } = await supabase.from('profiles').upsert({
        id: session.user.id,
        email: session.user.email,
        contact_person: formData.contact_person, // ⚡ SYNCED
        company_name: formData.company_name
      });

      if (upsertError) throw upsertError;

      // ⚡ HARD RELOAD FIX: Forces context memory to refresh after profile is saved
      if (session.user.email?.toLowerCase() === 'admin@shaikh.net.in') {
        window.location.href = '/admin';
      } else {
        sessionStorage.removeItem('returnTo');
        window.location.href = returnTo || '/directory';
      }
    } catch (error) {
      setError(error.message || 'Failed to save profile.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full relative">
        
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
             {/* ⚡ FIX: The Logo is no longer a clickable escape hatch when forcing Profile completion */}
             {step === 'profile' ? (
                <div className="flex items-center space-x-3 cursor-default">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white font-bold text-lg">TB</span>
                  </div>
                </div>
             ) : (
                <Link to="/" className="flex items-center space-x-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white font-bold text-lg">TB</span>
                  </div>
                </Link>
             )}
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {step === 'profile' ? 'One last step' : t('auth.reg_title', 'Create your Tariffbeater account')}
          </h2>
        </div>

        {step !== 'profile' && (
          <div className="mb-10 animate-fade-in-up">
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">
                {t('auth.free_benefits_title', 'Unlock the Canadian Core & Shell Network')}
              </h3>
              <p className="text-sm md:text-base text-slate-500 mt-2 font-medium">
                Create your 100% free account to get immediate access:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
              <div className="bg-white p-6 rounded-2xl border border-blue-100 shadow-sm hover:shadow-md hover:border-blue-300 transition-all relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-8 -mt-8 z-0 transition-transform group-hover:scale-110"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-5 border-b border-gray-100 pb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-xl">🏗️</div>
                    <h4 className="font-black text-blue-900 text-lg uppercase tracking-wide">{t('auth.for_buyers', 'For Buyers')}</h4>
                  </div>
                  <ul className="space-y-4 text-slate-700 font-medium">
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                      {t('auth.ben_sims', '3 Trade Simulations per month')}
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                      {t('auth.ben_dir', 'Full Directory Sourcing')}
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                      Concierge RFQ Sourcing Access
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-2xl border border-indigo-100 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-bl-full -mr-8 -mt-8 z-0 transition-transform group-hover:scale-110"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-5 border-b border-gray-100 pb-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-xl">🏭</div>
                    <h4 className="font-black text-indigo-900 text-lg uppercase tracking-wide">{t('auth.for_suppliers', 'For Suppliers')}</h4>
                  </div>
                  <ul className="space-y-4 text-slate-700 font-medium">
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                      {t('auth.ben_list', 'Profile listed in domestic directory')}
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                      {t('auth.ben_prod', 'Max Products: 50 (3 images, 1 PDF per SKU)')}
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                      {t('auth.ben_size', 'Max File Size: 5MB (No CAD/BIM)')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white py-10 px-8 shadow-xl rounded-2xl sm:px-12 border border-gray-100">
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start mb-6">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 text-sm text-red-700">{error}</div>
            </div>
          )}

          {step === 'email' && (
            <form className="space-y-6" onSubmit={handleEmailSubmit}>
              <Turnstile 
                ref={turnstileRef}
                siteKey="0x4AAAAAADUVQRUjAsFEjCBy" 
                options={{ theme: 'light', size: 'invisible' }}
                onSuccess={(token) => setTurnstileToken(token)}
              />
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.email', 'Work Email Address')}
                </label>
                <input
                  id="email" name="email" type="email" required
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-base"
                  placeholder="you@yourcompany.ca"
                  value={formData.email} onChange={handleChange} disabled={loading}
                />
              </div>
              <button
                type="submit" disabled={loading || !formData.email}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all"
              >
                {loading ? 'Processing...' : 'Send 6-Digit Code'}
              </button>
              <p className="mt-4 text-center text-xs text-gray-400">
                Protected by Cloudflare Turnstile. Subject to Cloudflare's <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noreferrer" className="underline hover:text-gray-500">Privacy Policy</a> and <a href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noreferrer" className="underline hover:text-gray-500">Terms of Service</a>.
              </p>
            </form>
          )}

          {step === 'otp' && (
            <form className="space-y-6" onSubmit={handleOtpSubmit}>
              <div className="text-center mb-6">
                <p className="text-sm text-gray-600">We sent a verification code to <span className="font-bold text-gray-900">{formData.email}</span></p>
              </div>
              <div>
                <input
                  type="text" required maxLength="6"
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-xl tracking-widest font-semibold"
                  placeholder="000000"
                  name="otp" value={formData.otp} onChange={(e) => setFormData(prev => ({ ...prev, otp: e.target.value.replace(/[^0-9]/g, '') }))} disabled={loading}
                />
              </div>
              <button
                type="submit" disabled={loading || formData.otp.length !== 6}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all"
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </button>
            </form>
          )}

          {step === 'profile' && (
            <form className="space-y-6" onSubmit={handleProfileSubmit}>
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6">
                <h3 className="text-sm font-bold text-amber-900 mb-1">Company Details Required</h3>
                <p className="text-xs text-amber-800">You must complete your profile to continue using the platform. These details are used to generate official Purchase Orders when you win or accept bids.</p>
              </div>

              {/* ⚡ FIX: Form mapping identically to database */}
              <div>
                <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  id="contact_person" name="contact_person" type="text" required
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-base"
                  value={formData.contact_person} onChange={handleChange} disabled={loading}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input
                  id="company_name" name="company_name" type="text" required
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-base"
                  value={formData.company_name} onChange={handleChange} disabled={loading}
                  placeholder="Acme Construction Corp."
                />
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="tos" name="tosAgreed" type="checkbox" required
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={formData.tosAgreed} onChange={handleChange}
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="tos" className="font-medium text-gray-700">
                      <span>{t('auth.agree_text', 'I agree to the')} </span>
                      <Link to="/terms" target="_blank" className="text-blue-600 hover:underline font-bold">
                        {t('auth.terms_link', 'Terms of Service')}
                      </Link>
                      <span> {t('auth.and_text', 'and')} </span>
                      <Link to="/privacy" target="_blank" className="text-blue-600 hover:underline font-bold">
                        {t('auth.privacy_link', 'Privacy Policy')}
                      </Link>.
                    </label>
                  </div>
                </div>
              </div>
              <button
                type="submit" disabled={loading || !formData.contact_person || !formData.company_name || !formData.tosAgreed}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all"
              >
                {loading ? 'Saving...' : 'Start Using TariffBeater'}
              </button>
            </form>
          )}

          {step === 'email' && (
            <div className="mt-6 text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{t('auth.already_registered', 'Already registered?')}</span>
                </div>
              </div>
              <Link
                to="/login"
                state={{ returnTo }} 
                className="font-medium text-blue-600 hover:text-blue-500 underline decoration-2 decoration-blue-100 hover:decoration-blue-500 transition-all"
              >
                {t('auth.sign_in_account', 'Sign in to your account')}
              </Link>
            </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;